import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { leadService } from '../services/leads'
import type { Lead, TelecallerWeight, CallLog } from '../../../types'
import { UserRole } from '../../../types'
import { LEAD_STATUS_COLORS, LEAD_STATUS_LABELS, priorityBadgeClass } from '../../../lib/constants'
import { Plus, Upload, Search, AlertTriangle, Shuffle, CheckCircle, X, Lightbulb, ChevronUp, ChevronDown, MessageSquare, BarChart2, Phone, Eye, SlidersHorizontal } from 'lucide-react'
import ImportWizard from '../components/ImportWizard'
import { authService } from '../../identity/services/auth'
import { telephonyService } from '../../telephony/services/telephony'
import { warmLeadsCache } from '../../../lib/useOfflineLeads'
import { useWindowWidth } from '../../../lib/useWindowWidth'

const SENTIMENT_STYLES: Record<string, { label: string; cls: string }> = {
  positive: { label: 'Positive', cls: 'badge-success' },
  neutral:  { label: 'Neutral',  cls: 'badge-neutral'  },
  negative: { label: 'Negative', cls: 'badge-danger'   },
}

function LeadCallIntelPanel({ callLog }: { callLog: CallLog | 'loading' | 'none' | undefined }) {
  if (!callLog || callLog === 'loading') {
    return (
      <div className="px-4 py-3" style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}>
        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          {callLog === 'loading' ? 'Loading call intelligence…' : 'Fetching…'}
        </p>
      </div>
    )
  }
  if (callLog === 'none') {
    return (
      <div className="px-4 py-3" style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}>
        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>No call logs found for this lead.</p>
      </div>
    )
  }
  const { transcript, analysis } = callLog
  return (
    <div style={{ background: 'var(--surface-subtle)', borderTop: '1px solid var(--border-default)' }}>
      {/* Summary strip */}
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
          <Lightbulb className="h-3 w-3" /> Latest call
        </span>
        {analysis?.customer_sentiment && (
          <span className={`${SENTIMENT_STYLES[analysis.customer_sentiment]?.cls ?? 'badge-neutral'} text-xs`}>
            {SENTIMENT_STYLES[analysis.customer_sentiment]?.label ?? analysis.customer_sentiment}
          </span>
        )}
        {analysis?.customer_intent && (
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Intent: <strong>{analysis.customer_intent.replace(/_/g, ' ')}</strong>
          </span>
        )}
        {analysis?.agent_score?.overall != null && (
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Agent score: <strong>{analysis.agent_score.overall}/5</strong>
          </span>
        )}
        {analysis?.follow_up_action && (
          <span
            className="text-xs px-2 py-0.5 rounded"
            style={{ background: 'var(--brand-50)', color: 'var(--brand-800)' }}
          >
            Next: {analysis.follow_up_action}
          </span>
        )}
        <span className="text-xs tabular-nums ml-auto" style={{ color: 'var(--text-tertiary)' }}>
          {new Date(callLog.created_at).toLocaleString()}
        </span>
      </div>

      {/* Transcript bubbles (compact) */}
      {transcript && (
        <div className="px-4 pb-3">
          <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
            <MessageSquare className="h-3 w-3" /> Transcript
          </p>
          <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
            {transcript.split('\n').map(l => l.trim()).filter(Boolean).map((line, i) => {
              const isAgent = line.startsWith('Agent:')
              const text = isAgent ? line.slice(6).trim() : line.startsWith('Customer:') ? line.slice(9).trim() : line
              return (
                <div key={i} className={`flex items-end gap-2 ${isAgent ? '' : 'flex-row-reverse'}`}>
                  <div
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                    style={isAgent
                      ? { background: 'var(--brand-50)', color: 'var(--brand-700)' }
                      : { background: 'var(--surface-muted)', color: 'var(--text-secondary)', border: '1px solid var(--border-default)' }}
                  >
                    {isAgent ? 'A' : 'C'}
                  </div>
                  <div
                    className="max-w-[70%] px-2.5 py-1.5 text-xs"
                    style={{
                      background: isAgent ? 'var(--brand-50)' : 'var(--surface-default)',
                      color: isAgent ? 'var(--brand-800)' : 'var(--text-primary)',
                      border: isAgent ? 'none' : '1px solid var(--border-default)',
                      borderRadius: isAgent ? '10px 10px 10px 3px' : '10px 10px 3px 10px',
                      lineHeight: '1.5',
                    }}
                  >
                    {text}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Coaching notes */}
      {analysis?.coaching_notes && (
        <div className="px-4 pb-3">
          <p className="text-xs font-medium mb-1.5 flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
            <BarChart2 className="h-3 w-3" /> Coaching notes
          </p>
          <div
            className="text-xs px-3 py-2 rounded-r-lg"
            style={{
              borderLeft: '3px solid var(--brand-primary)',
              background: 'var(--surface-muted)',
              color: 'var(--text-primary)',
              lineHeight: '1.6',
            }}
          >
            {analysis.coaching_notes}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Mobile card components ───────────────────────────────────────────────────

function priorityDotColor(score: number): string {
  if (score >= 70) return 'var(--danger-text)'
  if (score >= 40) return 'var(--warning-text)'
  return 'var(--success-text)'
}

function LeadCard({
  lead, expanded, onToggle, onView, onCall,
}: {
  lead: Lead
  expanded: boolean
  onToggle: () => void
  onView: () => void
  onCall: (e: React.MouseEvent) => void
}) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header — always visible */}
      <div className="flex items-center gap-2.5 px-3 py-3 cursor-pointer" onClick={onToggle}>
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: priorityDotColor(lead.priority_score) }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
            {lead.customer_name}
          </p>
          <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
            {lead.mobile}{lead.vehicle_reg_no ? ` · ${lead.vehicle_reg_no}` : ''}
          </p>
        </div>
        <span className={`${LEAD_STATUS_COLORS[lead.status] ?? 'badge-neutral'} shrink-0`} style={{ fontSize: 10 }}>
          {LEAD_STATUS_LABELS[lead.status] ?? lead.status.replace(/_/g, ' ')}
        </span>
        <ChevronDown
          className="h-4 w-4 shrink-0 transition-transform"
          style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
        />
      </div>
      {/* Expanded body */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
          {lead.vehicle_model && (
            <div className="flex justify-between text-xs py-1">
              <span style={{ color: 'var(--text-tertiary)' }}>Model</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lead.vehicle_model}</span>
            </div>
          )}
          <div className="flex justify-between text-xs py-1">
            <span style={{ color: 'var(--text-tertiary)' }}>Due date</span>
            <span style={{ color: lead.insurance_due_date ? 'var(--text-primary)' : 'var(--text-tertiary)', fontWeight: 500 }}>
              {lead.insurance_due_date
                ? new Date(lead.insurance_due_date).toLocaleDateString('en-IN')
                : '—'}
            </span>
          </div>
          <div className="flex justify-between text-xs py-1">
            <span style={{ color: 'var(--text-tertiary)' }}>Assigned to</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
              {lead.assigned_to_name ?? <span style={{ color: 'var(--warning-text)' }}>Unassigned</span>}
            </span>
          </div>
          <div className="flex justify-between text-xs py-1">
            <span style={{ color: 'var(--text-tertiary)' }}>Priority</span>
            <span className={`${priorityBadgeClass(lead.priority_score)} text-xs font-semibold`}>{lead.priority_score}</span>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              className="flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', background: 'var(--surface-card)' }}
              onClick={onCall}
            >
              <Phone className="h-3.5 w-3.5" /> Call
            </button>
            <button
              className="flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border transition-colors"
              style={{ borderColor: 'var(--brand-primary)', color: 'white', background: 'var(--brand-primary)' }}
              onClick={e => { e.stopPropagation(); onView() }}
            >
              <Eye className="h-3.5 w-3.5" /> View
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function LeadCardList({
  leads, loading, onNavigate, onCall,
}: {
  leads: Lead[]
  loading: boolean
  onNavigate: (id: number) => void
  onCall: (e: React.MouseEvent, leadId: number) => void
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }
  if (leads.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="empty-state"><p>No leads found</p></div>
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto pb-2 space-y-2">
      {leads.map(lead => (
        <LeadCard
          key={lead.id}
          lead={lead}
          expanded={expandedId === lead.id}
          onToggle={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
          onView={() => onNavigate(lead.id)}
          onCall={e => onCall(e, lead.id)}
        />
      ))}
    </div>
  )
}

// ── Page constants ────────────────────────────────────────────────────────────

const DUE_DATE_OPTIONS = [
  { label: 'All',     value: '' },
  { label: '7 days',  value: '7' },
  { label: '15 days', value: '15' },
  { label: '30 days', value: '30' },
]

export default function LeadsPage() {
  const [leads, setLeads]             = useState<Lead[]>([])
  const [total, setTotal]             = useState(0)
  const [page, setPage]               = useState(1)
  const [search, setSearch]           = useState('')
  const [loading, setLoading]         = useState(true)
  const [showImportWizard, setShowImportWizard] = useState(false)
  const [allocating, setAllocating]   = useState(false)
  const [allocResult, setAllocResult] = useState<string | null>(null)
  const [unassignedCount, setUnassigned] = useState(0)
  const [expandedCallLead, setExpandedCallLead] = useState<number | null>(null)
  const [callLogCache, setCallLogCache] = useState<Record<number, CallLog | 'loading' | 'none'>>({})


  // Filters
  const [assignedToFilter, setAssignedToFilter] = useState<string>('')
  const [dueDateFilter, setDueDateFilter]       = useState<string>('')
  const [telecallers, setTelecallers]           = useState<TelecallerWeight[]>([])
  const [filtersOpen, setFiltersOpen]           = useState(false)

  const navigate    = useNavigate()
  const isMobile    = useWindowWidth() < 768
  const currentUser = authService.getCurrentUser()
  const canAllocate    = [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.DEALER_PRINCIPAL].includes(currentUser?.role as UserRole)
  const canSeeAllLeads = currentUser?.role !== UserRole.TELECALLER
  const isManager      = [UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL].includes(currentUser?.role as UserRole)

  useEffect(() => {
    if (!canSeeAllLeads) return
    leadService.getTelecallerWeights().then(setTelecallers).catch(() => {})
  }, [canSeeAllLeads])

  const fetchLeads = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { search, page, status_group: 'active' }
      if (assignedToFilter === 'unassigned')  params.unassigned = true
      else if (assignedToFilter)              params.assigned_to = Number(assignedToFilter)
      if (dueDateFilter)                      params.due_within_days = Number(dueDateFilter)
      const data = await leadService.list(params)
      setLeads(data.items)
      setTotal(data.total)
      warmLeadsCache(data.items).catch(() => {})
    } finally {
      setLoading(false)
    }
  }, [search, page, assignedToFilter, dueDateFilter])

  const fetchUnassignedCount = useCallback(async () => {
    if (!canAllocate) return
    try {
      const data = await leadService.list({ unassigned: true, status_group: 'active', min_priority: 1, page: 1, page_size: 1 })
      setUnassigned(data.total)
    } catch { /* non-critical */ }
  }, [canAllocate])

  useEffect(() => { fetchLeads() }, [fetchLeads])
  useEffect(() => { fetchUnassignedCount() }, [fetchUnassignedCount])

  const handleAutoAllocate = async () => {
    setAllocating(true)
    setAllocResult(null)
    try {
      const result = await leadService.triggerAutoAllocate()
      setAllocResult(`${result.allocated} lead${result.allocated !== 1 ? 's' : ''} allocated`)
      fetchLeads()
      fetchUnassignedCount()
    } catch {
      setAllocResult('Allocation failed — please try again')
    } finally {
      setAllocating(false)
    }
  }

  const toggleCallExpand = async (e: React.MouseEvent, leadId: number) => {
    e.stopPropagation()
    if (expandedCallLead === leadId) { setExpandedCallLead(null); return }
    setExpandedCallLead(leadId)
    if (callLogCache[leadId]) return
    setCallLogCache(c => ({ ...c, [leadId]: 'loading' }))
    const log = await telephonyService.getLatestCallForLead(leadId)
    setCallLogCache(c => ({ ...c, [leadId]: log ?? 'none' }))
  }

  const resetPage = () => setPage(1)
  const totalPages = Math.max(1, Math.ceil(total / 20))
  // assignedToFilter: '' = all, 'unassigned' = unassigned, '123' = specific TC id
  const activeFilterCount = [!!assignedToFilter, !!dueDateFilter].filter(Boolean).length

  return (
    /*
     * h-full flex flex-col: page fills exactly the main viewport area.
     * Fixed-height blocks (header, search, filters) are shrink-0.
     * table-card gets flex-1 min-h-0 so it fills remaining space and
     * the table body is the only scroll container — no double scroll.
     */
    <div className="h-full flex flex-col gap-3">

      {/* ── Fixed header area ──────────────────────────────────────────── */}
      <div className="shrink-0 space-y-3">

        {/* Section header */}
        <div className="section-header" style={{ marginBottom: 0 }}>
          <div className="flex-1 min-w-0">
            <h1 className="page-title flex items-center gap-2">
              Active Leads
              {/* Mobile action icons — inline with title */}
              {isMobile && (
                <span className="flex items-center gap-1.5 ml-auto">
                  {canAllocate && (
                    <button
                      className="btn-icon"
                      onClick={handleAutoAllocate}
                      disabled={allocating}
                      title={allocating ? 'Allocating…' : 'Auto-Allocate'}
                      style={{ position: 'relative' }}
                    >
                      <Shuffle className={`h-4 w-4 ${allocating ? 'animate-spin' : ''}`} />
                      {unassignedCount > 0 && !allocating && (
                        <span style={{
                          position: 'absolute', top: -4, right: -4,
                          background: 'var(--danger-text)', color: '#fff',
                          borderRadius: '999px', fontSize: 9, fontWeight: 700,
                          minWidth: 15, height: 15, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          padding: '0 3px', lineHeight: 1,
                        }}>
                          {unassignedCount}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    className="btn-icon"
                    onClick={() => navigate('/leads/new')}
                    title="New Lead"
                    style={{ background: 'var(--brand-primary)', color: '#fff', border: 'none' }}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </span>
              )}
            </h1>
            <p className="page-subtitle">
              {total.toLocaleString()} active lead{total !== 1 ? 's' : ''}
              {unassignedCount > 0 && canAllocate && (
                <span style={{ color: 'var(--warning-text)', marginLeft: 8 }}>
                  · {unassignedCount} unassigned
                </span>
              )}
            </p>
          </div>
          {/* Desktop action buttons */}
          {!isMobile && (
            <div className="flex items-center gap-2">
              {canAllocate && (
                <button className="btn-secondary" onClick={handleAutoAllocate} disabled={allocating} style={{ position: 'relative' }}>
                  <Shuffle className={`h-3.5 w-3.5 ${allocating ? 'animate-spin' : ''}`} />
                  {allocating ? 'Allocating…' : 'Auto-Allocate'}
                  {unassignedCount > 0 && !allocating && (
                    <span style={{
                      position: 'absolute', top: -6, right: -6,
                      background: 'var(--danger-text)', color: '#fff',
                      borderRadius: '999px', fontSize: 10, fontWeight: 700,
                      minWidth: 18, height: 18, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      padding: '0 4px', lineHeight: 1,
                    }}>
                      {unassignedCount}
                    </span>
                  )}
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowImportWizard(true)}>
                <Upload className="h-3.5 w-3.5" />
                Import CSV / XLSX
              </button>
              <button className="btn-primary" onClick={() => navigate('/leads/new')}>
                <Plus className="h-3.5 w-3.5" /> New Lead
              </button>
            </div>
          )}
        </div>

        {/* Auto-allocate result */}
        {allocResult && (
          <div className={allocResult.includes('failed') ? 'alert-danger' : 'alert-success'}>
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>{allocResult}</span>
            <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setAllocResult(null)}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Search + filter toggle */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
              style={{ color: 'var(--text-tertiary)' }}
            />
            <input
              type="text"
              className="input pl-9"
              placeholder="Search by mobile or vehicle reg…"
              value={search}
              onChange={e => { setSearch(e.target.value); resetPage() }}
            />
          </div>
          {/* Filter toggle — always visible, shows active count badge */}
          <button
            onClick={() => setFiltersOpen(o => !o)}
            className="btn-icon shrink-0 relative"
            aria-label="Toggle filters"
            style={{
              color: activeFilterCount > 0 ? 'var(--brand-primary)' : 'var(--text-secondary)',
              border: `1px solid ${activeFilterCount > 0 ? 'var(--brand-primary)' : 'var(--border-default)'}`,
              background: activeFilterCount > 0 ? 'var(--brand-primary-subtle)' : 'transparent',
            }}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {activeFilterCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 rounded-full flex items-center justify-center font-bold"
                style={{ width: 16, height: 16, fontSize: 10, background: 'var(--brand-primary)', color: '#fff' }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Collapsible filter bar — always shown on desktop, toggleable on mobile */}
        {(!isMobile || filtersOpen) && (
          <div className="flex flex-wrap items-center gap-2">

            {/* Telecaller / Unassigned dropdown — managers only */}
            {canSeeAllLeads && (
              <select
                className="input"
                style={{ width: 'auto', minWidth: 150, height: 32, fontSize: 13, padding: '0 10px' }}
                value={assignedToFilter}
                onChange={e => { setAssignedToFilter(e.target.value); resetPage() }}
              >
                <option value="">All Telecallers</option>
                {unassignedCount > 0 && (
                  <option value="unassigned">
                    Unassigned ({unassignedCount})
                  </option>
                )}
                {telecallers.map(tc => (
                  <option key={tc.user_id} value={String(tc.user_id)}>{tc.name}</option>
                ))}
              </select>
            )}

            {/* Due Date toggle */}
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
              {DUE_DATE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setDueDateFilter(opt.value); resetPage() }}
                  style={{
                    padding: '5px 10px', fontSize: 13,
                    fontWeight: dueDateFilter === opt.value ? 600 : 400,
                    background: dueDateFilter === opt.value ? 'var(--brand-primary)' : 'var(--surface-default)',
                    color: dueDateFilter === opt.value ? '#fff' : 'var(--text-secondary)',
                    border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Clear filters */}
            {activeFilterCount > 0 && (
              <button
                className="btn-secondary"
                style={{ height: 32, fontSize: 12, padding: '0 10px' }}
                onClick={() => { setAssignedToFilter(''); setDueDateFilter(''); resetPage() }}
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Table / Card view ─────────────────────────────────────────────── */}
      {isMobile ? (
        <>
          <LeadCardList
            leads={leads}
            loading={loading}
            onNavigate={id => navigate(`/leads/${id}`)}
            onCall={(e, id) => toggleCallExpand(e, id)}
          />
          {/* Pagination for mobile card list */}
          <div
            className="shrink-0 flex items-center justify-between px-4 py-3"
            style={{ borderTop: '1px solid var(--border-default)' }}
          >
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              Page {page} of {totalPages} · {total.toLocaleString()} leads
            </p>
            <div className="flex gap-1.5">
              <button className="btn-secondary" style={{ height: '28px', fontSize: '12px', padding: '0 10px' }}
                disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </button>
              <button className="btn-secondary" style={{ height: '28px', fontSize: '12px', padding: '0 10px' }}
                disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="table-card flex-1 min-h-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th className="hidden sm:table-cell">Mobile</th>
                  <th className="hidden md:table-cell">Vehicle</th>
                  <th>Status</th>
                  <th className="hidden sm:table-cell">Priority</th>
                  <th className="hidden md:table-cell">Assigned To</th>
                  <th className="hidden sm:table-cell">Due Date</th>
                  <th className="hidden lg:table-cell">Source</th>
                  <th></th>
                  {isManager && <th></th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isManager ? 10 : 9} className="text-center py-10" style={{ color: 'var(--text-tertiary)' }}>
                      Loading…
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={isManager ? 10 : 9} className="text-center py-10" style={{ color: 'var(--text-tertiary)' }}>
                      No leads found
                    </td>
                  </tr>
                ) : (
                  leads.map(lead => (
                    <React.Fragment key={lead.id}>
                      <tr
                        className="clickable"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <td className="text-primary-data">
                          <p>{lead.customer_name}</p>
                          {lead.vehicle_model && (
                            <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                              {lead.vehicle_model}
                            </p>
                          )}
                        </td>
                        <td className="tabular-nums hidden sm:table-cell">{lead.mobile}</td>
                        <td className="hidden md:table-cell">{lead.vehicle_reg_no || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                        <td>
                          <span className={LEAD_STATUS_COLORS[lead.status] ?? 'badge-neutral'}>
                            {LEAD_STATUS_LABELS[lead.status] ?? lead.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="hidden sm:table-cell">
                          <span className={`${priorityBadgeClass(lead.priority_score)} text-xs font-semibold tabular-nums`}>
                            {lead.priority_score}
                          </span>
                        </td>
                        <td className="hidden md:table-cell">
                          {lead.assigned_to_name
                            ? <span style={{ color: 'var(--text-primary)' }}>{lead.assigned_to_name}</span>
                            : <span className="badge-warning">Unassigned</span>
                          }
                        </td>
                        <td className="tabular-nums hidden sm:table-cell">
                          {lead.insurance_due_date
                            ? new Date(lead.insurance_due_date).toLocaleDateString('en-IN')
                            : <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                          }
                        </td>
                        <td className="hidden lg:table-cell" style={{ color: 'var(--text-tertiary)' }}>
                          {lead.lead_source.replace(/_/g, ' ')}
                        </td>
                        <td>
                          {lead.dnd_status === 'registered' && (
                            <AlertTriangle className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} aria-label="DND registered" />
                          )}
                        </td>
                        {isManager && (
                          <td>
                            <button
                              className="btn-icon"
                              title="Show latest call intelligence"
                              onClick={e => toggleCallExpand(e, lead.id)}
                            >
                              {expandedCallLead === lead.id
                                ? <ChevronUp className="h-3.5 w-3.5" />
                                : <Lightbulb className="h-3.5 w-3.5" style={{ color: 'var(--brand-primary)' }} />
                              }
                            </button>
                          </td>
                        )}
                      </tr>

                      {isManager && expandedCallLead === lead.id && (
                        <tr>
                          <td colSpan={10} style={{ padding: 0 }}>
                            <LeadCallIntelPanel callLog={callLogCache[lead.id]} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — pinned at bottom of card */}
          <div
            className="shrink-0 flex items-center justify-between px-4 py-3"
            style={{ borderTop: '1px solid var(--border-default)' }}
          >
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              Page {page} of {totalPages} · {total.toLocaleString()} leads
            </p>
            <div className="flex gap-1.5">
              <button className="btn-secondary" style={{ height: '28px', fontSize: '12px', padding: '0 10px' }}
                disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </button>
              <button className="btn-secondary" style={{ height: '28px', fontSize: '12px', padding: '0 10px' }}
                disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import wizard */}
      {showImportWizard && (
        <ImportWizard
          onClose={() => setShowImportWizard(false)}
          onComplete={() => { fetchLeads(); setShowImportWizard(false) }}
        />
      )}
    </div>
  )
}
